import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { PolicyContent } from "../components/PolicyContent";
import { formatUK } from "../lib/ukdate";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Breadcrumb, BreadcrumbList, BreadcrumbItem, BreadcrumbLink, BreadcrumbSeparator, BreadcrumbPage } from "../components/ui/breadcrumb";
import { CheckCircle, Bookmark, ChatCircleDots, Warning, ClockCounterClockwise, Gear, DownloadSimple, PaperPlaneTilt, UsersThree, FilePdf } from "@phosphor-icons/react";
import { openPdf } from "../lib/download";

const FEEDBACK_KINDS = [
  { k: "question", label: "Ask a question" },
  { k: "suggestion", label: "Suggest an improvement" },
  { k: "mismatch", label: "Flag a mismatch with actual practice" },
  { k: "safety_concern", label: "Raise a safety concern" },
  { k: "cannot_follow", label: "Explain why I can't follow this" },
];

export default function PolicyDetailPage() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [ackOpen, setAckOpen] = useState(false);
  const [fbOpen, setFbOpen] = useState(false);
  const [fbKind, setFbKind] = useState("question");
  const [fbMode, setFbMode] = useState("understand_suggestion");
  const [fbBody, setFbBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [variant, setVariant] = useState(null); // null=auto, 'published', 'draft'
  const [issueOpen, setIssueOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [effDate, setEffDate] = useState("");
  const [freshAck, setFreshAck] = useState(true);

  const load = () => api.get(`/policies/${id}`, { params: { _t: Date.now(), variant: variant || undefined } }).then((r) => setD(r.data)).catch(() => toast.error("Could not load policy"));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id, variant]);

  if (!d) return <p className="text-slate-400 text-sm">Loading…</p>;
  const rev = d.current_revision;
  const p = d.personal;

  const doAck = async () => {
    setBusy(true);
    try { await api.post(`/policies/${id}/acknowledge`); toast.success("Acknowledged"); setAckOpen(false); await load(); }
    catch { toast.error("Could not acknowledge"); } finally { setBusy(false); }
  };
  const doFeedback = async () => {
    if (!fbBody.trim()) return toast.error("Please describe your feedback");
    setBusy(true);
    try {
      await api.post(`/policies/${id}/feedback`, { kind: fbKind, mode: fbKind === "question" ? fbMode : undefined, body: fbBody });
      toast.success("Sent to the policy owner"); setFbOpen(false); setFbBody(""); await load();
    } catch { toast.error("Could not send feedback"); } finally { setBusy(false); }
  };
  const toggleBookmark = async () => { await api.post(`/policies/${id}/bookmark`); load(); };

  const openIssue = async () => {
    setEffDate(new Date().toISOString().slice(0, 10));
    setFreshAck(d.working_revision?.requires_fresh_ack ?? true);
    setPreview(null);
    setIssueOpen(true);
    try { const r = await api.get(`/policies/${id}/assignment-preview`); setPreview(r.data); } catch { setPreview(null); }
  };
  const doIssue = async () => {
    const w = d.working_revision; if (!w) return;
    setBusy(true);
    try {
      const uk = effDate ? effDate.split("-").reverse().join("/") : undefined;
      if (w.status === "draft") await api.post(`/policies/revisions/${w.id}/submit`);
      if (w.status === "draft" || w.status === "review") await api.post(`/policies/revisions/${w.id}/approve`);
      await api.post(`/policies/revisions/${w.id}/publish`, { effective_date: uk, requires_fresh_ack: freshAck });
      toast.success("RAMS issued to the assigned workforce");
      setIssueOpen(false); setVariant(null); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not issue this RAMS"); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl">
      <Breadcrumb className="mb-4">
        <BreadcrumbList>
          <BreadcrumbItem><BreadcrumbLink asChild><Link to="/policies">Policy Library</Link></BreadcrumbLink></BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem><BreadcrumbPage className="truncate max-w-[200px]">{d.title}</BreadcrumbPage></BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <article className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <header className="px-6 sm:px-10 pt-8 pb-6 border-b border-slate-100 bg-gradient-to-b from-emerald-50/40 to-white">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="font-display text-2xl sm:text-3xl font-black text-slate-900 leading-tight">{d.title}</h1>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 font-mono text-xs text-slate-500">
                <span>{d.ref}</span>
                {rev && <><span>·</span><span>Revision {rev.revision}</span></>}
                {rev?.effective_date && <><span>·</span><span>Effective {formatUK(rev.effective_date)}</span></>}
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <button data-testid="bookmark-btn" onClick={toggleBookmark} title="Bookmark"
                className={`h-9 w-9 flex items-center justify-center rounded-lg border ${p.bookmarked ? "bg-emerald-50 border-emerald-300 text-emerald-700" : "border-slate-200 text-slate-400 hover:text-slate-600"}`}>
                <Bookmark size={18} weight={p.bookmarked ? "fill" : "regular"} />
              </button>
              {d.can_manage && (
                <Link to={`/policy-manager/doc/${id}`} data-testid="manage-link" className="h-9 px-3 flex items-center gap-1.5 rounded-lg border border-slate-200 text-slate-600 hover:text-slate-900 text-sm font-semibold"><Gear size={16} /> Manage</Link>
              )}
            </div>
          </div>

          {/* Three separated statuses, per spec */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-5">
            <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
              <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400">My acknowledgement</div>
              {p.acknowledged
                ? <div className="text-sm font-semibold text-emerald-800 flex items-center gap-1 mt-0.5"><CheckCircle size={15} weight="fill" /> Acknowledged{rev ? ` · Rev ${rev.revision}` : ""}</div>
                : p.awaiting_clarification
                ? <div className="text-sm font-semibold text-amber-800 mt-0.5">Awaiting clarification</div>
                : p.action_required
                ? <div className="text-sm font-semibold text-red-700 mt-0.5" data-testid="ack-required-badge">Acknowledgement required</div>
                : <div className="text-sm font-semibold text-slate-600 mt-0.5" data-testid="ack-status-text">{{ not_assigned: "Not assigned to you", reference: "Reference — no signature", no_published: "Not yet published", future_effective: "Opens when effective" }[p.ack_state] || "Not required"}</div>}
            </div>
            <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
              <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Publication</div>
              {rev ? (
                <div className="text-sm font-semibold text-slate-700 mt-0.5" data-testid="detail-publication">Rev {rev.revision}
                  <div className="text-[11px] font-normal text-slate-500 mt-0.5 leading-relaxed">
                    {rev.published_at && <>Issued {formatUK(rev.published_at)}<br /></>}
                    {rev.effective_date && <>Effective {formatUK(rev.effective_date)}</>}
                  </div>
                </div>
              ) : <div className="text-sm font-semibold text-slate-700 mt-0.5">Not published</div>}
            </div>
            <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
              <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Review</div>
              <div className="text-sm font-semibold text-slate-700 mt-0.5">{d.review_date ? `Due ${formatUK(d.review_date)}` : "Not scheduled"}</div>
            </div>
          </div>
          {rev?.change_summary && (
            <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2" data-testid="detail-change-summary">
              <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">What changed in this version</span>
              <p className="text-sm text-slate-700 mt-0.5">{rev.change_summary}</p>
            </div>
          )}
        </header>

        <div className="px-6 sm:px-10 py-8">
          {d.pdf_variants?.published && d.pdf_variants?.draft && (
            <div data-testid="pdf-variant-toggle" className="mb-4 inline-flex rounded-lg border border-slate-200 overflow-hidden text-sm">
              <button data-testid="variant-published" onClick={() => setVariant("published")}
                className={`px-3 py-1.5 font-semibold ${d.pdf_variant === "published" ? "bg-emerald-700 text-white" : "bg-white text-slate-600"}`}>Published version</button>
              <button data-testid="variant-draft" onClick={() => setVariant("draft")}
                className={`px-3 py-1.5 font-semibold ${d.pdf_variant === "draft" ? "bg-amber-600 text-white" : "bg-white text-slate-600"}`}>Working draft</button>
            </div>
          )}
          {d.pdf_is_draft && (
            <div data-testid="pdf-draft-banner" className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-900">
              <b>Working draft — not yet issued.</b> This is revision {d.pdf_revision} ({d.pdf_filename}). Staff can't see it yet. Use <b>Issue RAMS</b> below to publish it to the assigned workforce.
            </div>
          )}
          {d.content_format === "pdf" && !d.pdf_url && d.pdf_access !== false ? (
            <div data-testid="pdf-retrieve-error" className="rounded-lg border border-rose-200 bg-rose-50 p-6 text-sm text-rose-800">
              This document's PDF could not be retrieved right now.
              <Button data-testid="pdf-retry" variant="outline" onClick={load} className="ml-3 h-8">Retry</Button>
            </div>
          ) : (
            <PolicyContent body={rev?.body} pdfUrl={d.pdf_url} pdfFilename={d.pdf_filename} isPdf={d.content_format === "pdf"} pdfAccess={d.pdf_access} />
          )}
        </div>

        {/* Action bar */}
        <div className="sticky bottom-0 bg-white/95 backdrop-blur border-t border-slate-200 px-6 sm:px-10 py-4 flex flex-wrap gap-3 items-center">
          {p.action_required && (d.content_format !== "pdf" || d.pdf_url) && (
            <Button data-testid="acknowledge-open" onClick={() => setAckOpen(true)} className="bg-emerald-700 hover:bg-emerald-800 h-11 px-6 font-bold">
              {(d.ack_type === "receipt") ? "Confirm receipt" : "Read & acknowledge"}
            </Button>
          )}
          {p.action_required && d.content_format === "pdf" && !d.pdf_url && (
            <span data-testid="ack-blocked-nopdf" className="text-sm text-rose-700">You can't acknowledge until the PDF loads — tap Retry above.</span>
          )}
          {!p.action_required && !p.acknowledged && p.ack_message && (
            <span data-testid="ack-unavailable" className="text-sm text-slate-500">{p.ack_message}</span>
          )}
          {p.acknowledged && <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-800"><CheckCircle size={18} weight="fill" /> You acknowledged this{rev ? ` (Rev ${rev.revision})` : ""}{p.acknowledged_at ? ` on ${formatUK(p.acknowledged_at)}` : ""}</span>}
          {d.can_manage && d.working_revision && (
            <Button data-testid="issue-rams" onClick={openIssue} className="bg-blue-700 hover:bg-blue-800 h-11 px-6 font-bold gap-1.5">
              <PaperPlaneTilt size={17} /> {d.working_revision.status === "approved" ? "Issue RAMS" : "Approve & issue RAMS"}
            </Button>
          )}
          <Button data-testid="feedback-open" variant="outline" onClick={() => setFbOpen(true)} className="h-11 gap-1.5"><ChatCircleDots size={17} /> Ask / suggest</Button>
          <Button data-testid="export-pdf" variant="outline" onClick={() => openPdf(`/policies/${id}/export.pdf`)} className="h-11 gap-1.5"><DownloadSimple size={17} /> Export PDF</Button>
        </div>
      </article>

      {/* Revision history */}
      <section className="mt-6 bg-white border border-slate-200 rounded-xl p-5">
        <h3 className="font-display font-bold text-slate-800 flex items-center gap-2 mb-3"><ClockCounterClockwise size={18} /> Revision history</h3>
        <div className="flex flex-col divide-y divide-slate-100">
          {d.revisions.map((r) => (
            <div key={r.id} data-testid={`rev-${r.id}`} className="py-2 flex items-center justify-between gap-3">
              <div><span className="font-mono text-sm font-semibold text-slate-700">Rev {r.revision}</span>
                {r.change_summary && <span className="text-sm text-slate-500"> · {r.change_summary}</span>}</div>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`text-[10px] font-bold uppercase rounded px-1.5 py-0.5 ${r.status === "published" ? "bg-emerald-50 text-emerald-800" : "bg-slate-100 text-slate-500"}`}>{r.status}</span>
                {r.is_current && <span className="text-[10px] font-bold uppercase rounded px-1.5 py-0.5 bg-slate-900 text-white">Current</span>}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* My feedback thread */}
      {d.my_feedback?.length > 0 && (
        <section className="mt-6 bg-white border border-slate-200 rounded-xl p-5">
          <h3 className="font-display font-bold text-slate-800 mb-3">My questions & feedback</h3>
          <div className="flex flex-col gap-3">
            {d.my_feedback.map((f) => (
              <div key={f.id} data-testid={`myfb-${f.id}`} className="rounded-lg border border-slate-200 p-3">
                <div className="flex items-center justify-between"><span className="text-xs font-bold uppercase text-slate-500">{f.kind.replace(/_/g, " ")}</span>
                  <span className={`text-[10px] font-bold uppercase rounded px-1.5 py-0.5 ${f.status === "closed" ? "bg-slate-100 text-slate-500" : f.reply ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-800"}`}>{f.status}</span></div>
                <p className="text-sm text-slate-700 mt-1">{f.body}</p>
                {f.reply && <div className="mt-2 pl-3 border-l-2 border-emerald-300 text-sm text-slate-600"><span className="font-semibold text-emerald-800">Owner reply: </span>{f.reply}</div>}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Issue RAMS dialog (inline publication — no need to go to Authoring) */}
      <Dialog open={issueOpen} onOpenChange={setIssueOpen}>
        <DialogContent data-testid="issue-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><FilePdf size={20} className="text-blue-700" /> Issue this RAMS</DialogTitle></DialogHeader>
          <div className="text-sm text-slate-600">
            You're issuing <b>{d.title}</b> (revision {d.working_revision?.revision}). {d.current_revision ? "This will supersede the current published version." : "This will publish it for the first time."}
            {d.working_revision && d.working_revision.status !== "approved" && <div className="mt-1 text-amber-700">It will be sent for approval and approved as part of issuing (you have publication rights).</div>}
          </div>
          <label className="text-xs font-semibold text-slate-600">Effective date (UK)
            <input data-testid="issue-effective" type="date" value={effDate} onChange={(e) => setEffDate(e.target.value)} className="mt-1 w-full h-10 rounded-md border border-slate-200 px-3 text-sm" />
          </label>
          {d.current_revision && (
            <label className="flex items-start gap-2 text-sm text-slate-700">
              <input data-testid="issue-fresh-ack" type="checkbox" checked={freshAck} onChange={(e) => setFreshAck(e.target.checked)} className="mt-0.5" />
              <span>Require <b>fresh acknowledgement</b> for this revision. Uncheck for a minor correction — prior signatures then carry forward and stay linked to the revision they signed.</span>
            </label>
          )}
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm" data-testid="issue-audience">
            <div className="font-semibold text-slate-800 flex items-center gap-1.5 mb-1"><UsersThree size={16} /> Included workforce</div>
            {preview === null ? <div className="text-slate-400">Loading audience…</div>
              : preview.included_count === 0 ? <div className="text-amber-700">No one is currently assigned to this document's client/job. It will publish but require nobody until you set an audience rule and staff are assigned.</div>
              : (<>
                  <div className="text-emerald-800 mb-1"><b>{preview.included_count}</b> people will be required:</div>
                  <ul className="list-disc pl-5 text-slate-700 max-h-40 overflow-auto">{preview.included.slice(0, 30).map((p2) => <li key={p2.profile_id}>{p2.name} <span className="text-slate-400">— {p2.sources?.join(", ")}</span></li>)}</ul>
                  <div className="text-slate-500 mt-2">{preview.excluded_count} not included (not in this client/job workforce, or excepted).</div>
                </>)}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIssueOpen(false)}>Cancel</Button>
            <Button data-testid="issue-confirm" disabled={busy} onClick={doIssue} className="bg-blue-700 hover:bg-blue-800">{busy ? "Issuing…" : "Issue RAMS"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Acknowledge dialog */}
      <Dialog open={ackOpen} onOpenChange={setAckOpen}>
        <DialogContent data-testid="ack-dialog">
          <DialogHeader><DialogTitle>{d.ack_type === "receipt" ? "Confirm receipt" : "Acknowledge policy"}</DialogTitle></DialogHeader>
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 text-sm text-slate-700">{d.ack_wording}</div>
          <p className="text-xs text-slate-500">Signing as yourself. This records your name, the exact wording above and revision {rev?.revision}.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAckOpen(false)}>Cancel</Button>
            <Button data-testid="ack-confirm" disabled={busy} onClick={doAck} className="bg-emerald-700 hover:bg-emerald-800">{busy ? "Saving…" : "I agree"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Feedback dialog */}
      <Dialog open={fbOpen} onOpenChange={setFbOpen}>
        <DialogContent data-testid="feedback-dialog">
          <DialogHeader><DialogTitle>Send feedback to the policy owner</DialogTitle></DialogHeader>
          <select data-testid="fb-kind" value={fbKind} onChange={(e) => setFbKind(e.target.value)} className="h-10 rounded-md border border-slate-200 px-3 text-sm">
            {FEEDBACK_KINDS.map((f) => <option key={f.k} value={f.k}>{f.label}</option>)}
          </select>
          {fbKind === "question" && (
            <div className="flex flex-col gap-2 rounded-lg bg-slate-50 border border-slate-200 p-3">
              <label className="flex items-start gap-2 text-sm cursor-pointer"><input type="radio" name="mode" checked={fbMode === "understand_suggestion"} onChange={() => setFbMode("understand_suggestion")} className="mt-1" data-testid="fb-mode-a" /><span><b>I understand this but have a suggestion.</b> You can still acknowledge.</span></label>
              <label className="flex items-start gap-2 text-sm cursor-pointer"><input type="radio" name="mode" checked={fbMode === "need_clarification"} onChange={() => setFbMode("need_clarification")} className="mt-1" data-testid="fb-mode-b" /><span><b>I need clarification before I can acknowledge.</b> Shows as awaiting clarification.</span></label>
            </div>
          )}
          {(fbKind === "safety_concern" || fbKind === "cannot_follow") && (
            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 flex gap-1.5"><Warning size={15} weight="bold" className="shrink-0 mt-0.5" /> Raising this does not grant an exemption or authorise alternative working. The owner will respond.</p>
          )}
          <Textarea data-testid="fb-body" value={fbBody} onChange={(e) => setFbBody(e.target.value)} placeholder="Describe your question, suggestion or concern…" className="min-h-[120px]" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setFbOpen(false)}>Cancel</Button>
            <Button data-testid="fb-submit" disabled={busy} onClick={doFeedback} className="bg-emerald-700 hover:bg-emerald-800">{busy ? "Sending…" : "Send"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
